import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient, type ManagedClusterKind } from '../../lib/api-client';

type ClusterRef = {
  tenantCode: string;
  instanceName: string;
  cloudProvider: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  cluster: ClusterRef | null;
  clusterKind?: ManagedClusterKind;
};

type ClusterSchedule = {
  id: string;
  workflowName: string;
  schedule: string;
  enabled: boolean;
  createdAt?: string;
  parameters?: Record<string, any>;
};

function cronFrom(recurrence: 'weekly' | 'monthly_first', dayOfWeek: string, time: string): string {
  const [hh, mm] = String(time || '00:00').split(':');
  const h = Number(hh) || 0;
  const m = Number(mm) || 0;
  if (recurrence === 'weekly') return `${m} ${h} * * ${dayOfWeek}`;
  return `${m} ${h} 1 * *`;
}

function describeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, _mon, dow] = parts;
  const hh = String(hour).padStart(2, '0');
  const mm = String(min).padStart(2, '0');
  if (dom === '1' && dow === '*') return `First day of month at ${hh}:${mm}`;
  if (dom === '*' && dow !== '*') {
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][Number(dow)] || dow;
    return `Every ${day} at ${hh}:${mm}`;
  }
  return cron;
}

type DayUptimeInfo = {
  day: string;
  dayIndex: number;
  events: Array<{ time: string; action: 'start' | 'stop' }>;
  ranges: Array<{ startHour: number; endHour: number }>;
};

function parseUptimeGrid(schedules: ClusterSchedule[]): DayUptimeInfo[] {
  const byDay = new Map<number, Array<{ time: string; action: 'start' | 'stop' }>>();
  for (const d of [0, 1, 2, 3, 4, 5, 6]) byDay.set(d, []);

  for (const s of schedules) {
    if (!s.enabled) continue;
    const action = s.workflowName === 'oc-cluster-start' ? 'start' : s.workflowName === 'oc-cluster-stop' ? 'stop' : null;
    if (!action) continue;
    const parts = String(s.schedule || '').trim().split(/\s+/);
    if (parts.length !== 5) continue;
    const [min, hour, dom, _mon, dow] = parts;
    if (dom === '*' && dow !== '*') {
      const day = Number(dow);
      if (!Number.isNaN(day) && byDay.has(day)) {
        byDay.get(day)!.push({ time: `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`, action });
      }
    }
  }

  // Mon=1 first, then Tue..Sat, then Sun=0
  const ordered = [1, 2, 3, 4, 5, 6, 0];
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const timeToHour = (t: string) => {
    const [hh, mm] = t.split(':').map(Number);
    return hh + mm / 60;
  };

  // First pass: determine end-of-day state for each day to compute carry-over.
  // Start assumption: stopped (no green unless a start event is scheduled).
  // We need two passes to handle the circular week (Sun carry into Mon etc.).
  // Two iterations of the full week is sufficient to stabilise carry-over state.
  let carryIsUp = false;

  // Run the week twice so Sunday→Monday carry-over stabilises
  for (let pass = 0; pass < 2; pass++) {
    for (const dayIndex of ordered) {
      const events = (byDay.get(dayIndex) || []).sort((a, b) => a.time.localeCompare(b.time));
      let isUp = carryIsUp;
      for (const ev of events) {
        if (ev.action === 'start') isUp = true;
        else if (ev.action === 'stop') isUp = false;
      }
      carryIsUp = isUp;
    }
  }

  // Second pass: build actual ranges using the stabilised carry-over
  const result: DayUptimeInfo[] = [];

  for (const dayIndex of ordered) {
    const events = (byDay.get(dayIndex) || []).sort((a, b) => a.time.localeCompare(b.time));
    const ranges: Array<{ startHour: number; endHour: number }> = [];

    let isUp = carryIsUp;
    let cursor = 0; // only meaningful when isUp transitions from false→true

    for (const ev of events) {
      const h = timeToHour(ev.time);
      if (ev.action === 'start' && !isUp) {
        cursor = h;
        isUp = true;
      } else if (ev.action === 'stop' && isUp) {
        ranges.push({ startHour: cursor, endHour: h });
        isUp = false;
      }
      // duplicate start while already up, or stop while already down → ignore
    }

    // Carry green bar to end of day if still up
    if (isUp) {
      ranges.push({ startHour: cursor, endHour: 24 });
    }

    // Advance carry for next day
    carryIsUp = isUp;

    result.push({ day: names[dayIndex], dayIndex, events, ranges });
  }

  return result;
}

function WeeklyUptimeGrid({ data }: { data: DayUptimeInfo[] }) {
  const hours = [0, 3, 6, 9, 12, 15, 18, 21, 24];

  return (
    <div className="space-y-1">
      {/* Hour labels */}
      <div className="flex items-center">
        <div className="w-10 shrink-0" />
        <div className="flex-1 relative h-5">
          {hours.map((h) => (
            <span
              key={h}
              className="absolute text-[10px] text-gray-400 -translate-x-1/2 tabular-nums"
              style={{ left: `${(h / 24) * 100}%` }}
            >
              {String(h).padStart(2, '0')}
            </span>
          ))}
        </div>
      </div>
      {/* Day rows */}
      {data.map((d) => (
        <div key={d.dayIndex} className="flex items-center gap-2">
          <span className="w-10 shrink-0 text-xs font-medium text-gray-400 text-right">{d.day}</span>
          <div className="flex-1 relative h-6 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
            {/* Hour gridlines */}
            {hours.slice(1, -1).map((h) => (
              <div
                key={h}
                className="absolute top-0 bottom-0 w-px bg-gray-200 dark:bg-gray-700"
                style={{ left: `${(h / 24) * 100}%` }}
              />
            ))}
            {/* Uptime ranges */}
            {d.ranges.map((r, i) => (
              <div
                key={i}
                className="absolute top-0.5 bottom-0.5 rounded-sm bg-emerald-500/80 dark:bg-emerald-400/70"
                style={{
                  left: `${(r.startHour / 24) * 100}%`,
                  width: `${((r.endHour - r.startHour) / 24) * 100}%`,
                }}
              />
            ))}
            {/* Event markers */}
            {d.events.map((ev, i) => {
              const [h, m] = ev.time.split(':').map(Number);
              const pct = ((h + m / 60) / 24) * 100;
              return (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 w-0.5 z-10"
                  style={{ left: `${pct}%` }}
                  title={`${ev.action} @ ${ev.time}`}
                >
                  <div
                    className={`w-full h-full ${
                      ev.action === 'start'
                        ? 'bg-emerald-600 dark:bg-emerald-300'
                        : 'bg-red-500 dark:bg-red-400'
                    }`}
                  />
                </div>
              );
            })}
            {/* "No schedule" label */}
            {d.ranges.length === 0 && d.events.length === 0 && (
              <span className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-400">
                no schedule
              </span>
            )}
          </div>
        </div>
      ))}
      {/* Legend */}
      <div className="flex items-center gap-4 pt-1 pl-12">
        <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className="inline-block w-3 h-2 rounded-sm bg-emerald-500/80 dark:bg-emerald-400/70" /> Running
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className="inline-block w-3 h-2 rounded-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" /> Stopped
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className="inline-block w-0.5 h-3 bg-emerald-600 dark:bg-emerald-300" /> Start
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className="inline-block w-0.5 h-3 bg-red-500 dark:bg-red-400" /> Stop
        </span>
      </div>
    </div>
  );
}

export function UptimeScheduleDialog({ open, onClose, cluster, clusterKind }: Props) {
  const [schedules, setSchedules] = useState<ClusterSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'start' | 'stop'>('start');
  const [recurrence, setRecurrence] = useState<'weekly' | 'monthly_first'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [time, setTime] = useState('08:00');

  const uptimeGrid = useMemo(() => parseUptimeGrid(schedules), [schedules]);

  const load = async () => {
    if (!cluster) return;
    setLoading(true);
    try {
      const res = await apiClient.getOpenshiftClusterSchedules(cluster.tenantCode, cluster.instanceName, cluster.cloudProvider, clusterKind);
      setSchedules(res.schedules || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void load();
  }, [open, cluster?.tenantCode, cluster?.instanceName, cluster?.cloudProvider, clusterKind]);

  const onCreate = async () => {
    if (!cluster) return;
    const cronExpression = cronFrom(recurrence, dayOfWeek, time);
    await apiClient.createOpenshiftClusterSchedule(cluster.tenantCode, cluster.instanceName, cluster.cloudProvider, {
      action,
      cronExpression,
    }, clusterKind);
    await load();
  };

  const onDelete = async (id: string) => {
    if (!cluster) return;
    await apiClient.deleteOpenshiftClusterSchedule(cluster.tenantCode, cluster.instanceName, cluster.cloudProvider, id, clusterKind);
    await load();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-full md:w-[58vw] md:max-w-[58vw] h-[88vh]">
        <DialogHeader className="px-6 pt-8 pb-6 border-b border-gray-200 dark:border-gray-700">
          <DialogTitle>Uptime Schedule{cluster ? ` - ${cluster.instanceName}` : ''}</DialogTitle>
        </DialogHeader>
        <DialogBody className="px-6 py-4 space-y-4 overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select className="border rounded px-3 py-2 bg-transparent" value={action} onChange={(e) => setAction(e.target.value as any)}>
              <option value="start">Start</option>
              <option value="stop">Stop</option>
            </select>
            <select className="border rounded px-3 py-2 bg-transparent" value={recurrence} onChange={(e) => setRecurrence(e.target.value as any)}>
              <option value="weekly">Weekly</option>
              <option value="monthly_first">First day of month</option>
            </select>
            {recurrence === 'weekly' ? (
              <select className="border rounded px-3 py-2 bg-transparent" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
                <option value="0">Sunday</option>
              </select>
            ) : (
              <div className="border rounded px-3 py-2 text-sm text-gray-600 dark:text-gray-300">First day of month</div>
            )}
            <input className="border rounded px-3 py-2 bg-transparent" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div>
            <Button onClick={() => void onCreate()}>Add Schedule</Button>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold text-sm">Weekly Uptime</h4>
            <WeeklyUptimeGrid data={uptimeGrid} />
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold">Schedules</h4>
            {loading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : schedules.length === 0 ? (
              <div className="text-sm text-gray-500">No schedules</div>
            ) : (
              <div className="space-y-2">
                {schedules.map((s) => (
                  <div key={s.id} className="border rounded p-3 flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-medium">{s.workflowName === 'oc-cluster-start' ? 'Start' : 'Stop'}</div>
                      <div className="text-gray-600 dark:text-gray-300">{describeCron(s.schedule)}</div>
                    </div>
                    <Button variant="secondary" onClick={() => void onDelete(s.id)}>Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
